import { useTabs, TAB_TYPES } from '@/context/TabsContext';
import { X, FileText, LogOut, LogIn, Plus, User, Eye, Edit, 
  LayoutDashboard, Calendar, Users, Bed, UtensilsCrossed, 
  ClipboardList, Store, History, CreditCard, Ticket, Settings,
  BarChart3, Calculator, Building2, Hotel, Receipt, BookOpen, Wallet, Shield, Wifi
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import CheckoutTab from './tabs/CheckoutTab';
import CheckinTab from './tabs/CheckinTab';
import NewBookingTab from './tabs/NewBookingTab';
import InvoiceViewTab from './tabs/InvoiceViewTab';
import GuestDetailsTab from './tabs/GuestDetailsTab';
import GuestEditTab from './tabs/GuestEditTab';
import BookingDetailsTab from './tabs/BookingDetailsTab';
import PageTab from './tabs/PageTab';
import RoomForm from '@/pages/RoomForm';

const TAB_ICONS = {
  // Action tabs
  [TAB_TYPES.CHECKOUT]: LogOut,
  [TAB_TYPES.CHECKIN]: LogIn,
  [TAB_TYPES.NEW_BOOKING]: Plus,
  [TAB_TYPES.EDIT_BOOKING]: Edit,
  [TAB_TYPES.INVOICE_VIEW]: FileText,
  [TAB_TYPES.GUEST_DETAILS]: User,
  [TAB_TYPES.GUEST_EDIT]: Edit,
  [TAB_TYPES.GUEST_ADD]: Plus,
  [TAB_TYPES.BOOKING_DETAILS]: Eye,
  [TAB_TYPES.ROOM_ADD]: Plus,
  [TAB_TYPES.ROOM_EDIT]: Edit,
  // Page tabs
  [TAB_TYPES.PAGE_DASHBOARD]: LayoutDashboard,
  [TAB_TYPES.PAGE_BOOKINGS]: Calendar,
  [TAB_TYPES.PAGE_GUESTS]: Users,
  [TAB_TYPES.PAGE_ROOMS]: Bed,
  [TAB_TYPES.PAGE_ROOM_TYPES]: Bed,
  [TAB_TYPES.PAGE_SERVICES]: ClipboardList,
  [TAB_TYPES.PAGE_SERVICE_ORDERS]: ClipboardList,
  [TAB_TYPES.PAGE_RESTAURANTS]: Store,
  [TAB_TYPES.PAGE_RESTAURANT_TABLES]: UtensilsCrossed,
  [TAB_TYPES.PAGE_ORDER_HISTORY]: History,
  [TAB_TYPES.PAGE_CREDIT_ACCOUNTS]: CreditCard,
  [TAB_TYPES.PAGE_SUPPORT_TICKETS]: Ticket,
  [TAB_TYPES.PAGE_USERS]: Users,
  [TAB_TYPES.PAGE_ID_TYPES]: FileText,
  [TAB_TYPES.PAGE_BUDGET]: Calculator,
  [TAB_TYPES.PAGE_SETTINGS]: Settings,
  [TAB_TYPES.PAGE_FINANCE_SETUP]: Calculator,
  [TAB_TYPES.PAGE_COMPANIES]: Building2,
  [TAB_TYPES.PAGE_HOTELS]: Hotel,
  [TAB_TYPES.PAGE_FINANCIAL_REPORTS]: BarChart3,
  [TAB_TYPES.PAGE_PENDING_POSTINGS]: Receipt,
  [TAB_TYPES.PAGE_LEDGERS]: BookOpen,
  [TAB_TYPES.PAGE_INVOICES]: Receipt,
  [TAB_TYPES.PAGE_JOURNAL_ENTRIES]: Wallet,
  [TAB_TYPES.PAGE_CREATE_HOTEL]: Plus,
  [TAB_TYPES.PAGE_HOTEL_DETAILS]: Hotel,
  [TAB_TYPES.PAGE_PROPERTY_CONFIGURE]: Settings,
  [TAB_TYPES.PAGE_AUDIT_TRAIL]: Shield,
  [TAB_TYPES.PAGE_LIVE_SESSIONS]: Wifi,
};

const TAB_COLORS = {
  // Action tabs
  [TAB_TYPES.CHECKOUT]: 'text-purple-600 border-purple-500',
  [TAB_TYPES.CHECKIN]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.NEW_BOOKING]: 'text-orange-600 border-orange-500',
  [TAB_TYPES.EDIT_BOOKING]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.INVOICE_VIEW]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.GUEST_DETAILS]: 'text-cyan-600 border-cyan-500',
  [TAB_TYPES.GUEST_EDIT]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.GUEST_ADD]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.BOOKING_DETAILS]: 'text-indigo-600 border-indigo-500',
  [TAB_TYPES.ROOM_ADD]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.ROOM_EDIT]: 'text-orange-600 border-orange-500',
  // Page tabs - neutral colors
  [TAB_TYPES.PAGE_DASHBOARD]: 'text-gray-700 border-gray-500',
  [TAB_TYPES.PAGE_BOOKINGS]: 'text-orange-600 border-orange-500',
  [TAB_TYPES.PAGE_GUESTS]: 'text-cyan-600 border-cyan-500',
  [TAB_TYPES.PAGE_ROOMS]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.PAGE_ROOM_TYPES]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.PAGE_SERVICES]: 'text-violet-600 border-violet-500',
  [TAB_TYPES.PAGE_SERVICE_ORDERS]: 'text-violet-600 border-violet-500',
  [TAB_TYPES.PAGE_RESTAURANTS]: 'text-rose-600 border-rose-500',
  [TAB_TYPES.PAGE_RESTAURANT_TABLES]: 'text-rose-600 border-rose-500',
  [TAB_TYPES.PAGE_ORDER_HISTORY]: 'text-amber-600 border-amber-500',
  [TAB_TYPES.PAGE_CREDIT_ACCOUNTS]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.PAGE_SUPPORT_TICKETS]: 'text-pink-600 border-pink-500',
  [TAB_TYPES.PAGE_USERS]: 'text-indigo-600 border-indigo-500',
  [TAB_TYPES.PAGE_ID_TYPES]: 'text-gray-600 border-gray-500',
  [TAB_TYPES.PAGE_BUDGET]: 'text-teal-600 border-teal-500',
  [TAB_TYPES.PAGE_FINANCE_SETUP]: 'text-teal-600 border-teal-500',
  [TAB_TYPES.PAGE_COMPANIES]: 'text-slate-600 border-slate-500',
  [TAB_TYPES.PAGE_HOTELS]: 'text-amber-600 border-amber-500',
  [TAB_TYPES.PAGE_FINANCIAL_REPORTS]: 'text-green-600 border-green-500',
  [TAB_TYPES.PAGE_PENDING_POSTINGS]: 'text-yellow-600 border-yellow-500',
  [TAB_TYPES.PAGE_LEDGERS]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.PAGE_INVOICES]: 'text-blue-600 border-blue-500',
  [TAB_TYPES.PAGE_JOURNAL_ENTRIES]: 'text-purple-600 border-purple-500',
  [TAB_TYPES.PAGE_CREATE_HOTEL]: 'text-emerald-600 border-emerald-500',
  [TAB_TYPES.PAGE_HOTEL_DETAILS]: 'text-amber-600 border-amber-500',
  [TAB_TYPES.PAGE_PROPERTY_CONFIGURE]: 'text-orange-600 border-orange-500',
  [TAB_TYPES.PAGE_AUDIT_TRAIL]: 'text-indigo-600 border-indigo-500',
  [TAB_TYPES.PAGE_LIVE_SESSIONS]: 'text-green-600 border-green-500',
};

// Check if a tab type is a page tab
const isPageTab = (type) => type?.startsWith('page-');

export default function TabsContainer() {
  const { tabs, activeTabId, setActiveTabId, closeTab, hasTabs } = useTabs();

  if (!hasTabs) {
    return null;
  }

  const activeTab = tabs.find(t => t.id === activeTabId);

  const renderTabContent = (tab) => {
    if (!tab) return null;

    // Handle page tabs
    if (isPageTab(tab.type)) {
      return <PageTab key={tab.id} tabId={tab.id} data={tab.data} tabType={tab.type} />;
    }

    // Handle action tabs
    switch (tab.type) {
      case TAB_TYPES.CHECKOUT:
        return <CheckoutTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.CHECKIN:
        return <CheckinTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.NEW_BOOKING:
      case TAB_TYPES.EDIT_BOOKING:
        return <NewBookingTab key={tab.id} tabId={tab.id} data={tab.data} isEdit={tab.type === TAB_TYPES.EDIT_BOOKING} />;
      case TAB_TYPES.INVOICE_VIEW:
        return <InvoiceViewTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.GUEST_DETAILS:
        return <GuestDetailsTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.GUEST_EDIT:
        return <GuestEditTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.GUEST_ADD:
        return <GuestEditTab key={tab.id} tabId={tab.id} data={tab.data} isNew={true} />;
      case TAB_TYPES.BOOKING_DETAILS:
        return <BookingDetailsTab key={tab.id} tabId={tab.id} data={tab.data} />;
      case TAB_TYPES.ROOM_ADD:
      case TAB_TYPES.ROOM_EDIT:
        return <RoomForm key={tab.id} tabData={tab.data} />;
      default:
        return <div className="p-4">Unknown tab type: {tab.type}</div>;
    }
  };

  return (
    <div data-testid="tabs-container" className="fixed z-40 bg-white flex flex-col" style={{ left: '15rem', top: '3.5rem', right: 0, bottom: 0 }}>
      {/* Tab Bar */}
      <div className="bg-gray-100 border-b border-gray-200 flex-shrink-0">
        <ScrollArea className="w-full">
          <div className="flex items-center h-10 px-2 gap-1">
            {tabs.map((tab) => {
              const Icon = TAB_ICONS[tab.type] || FileText;
              const isActive = tab.id === activeTabId;
              const colorClass = TAB_COLORS[tab.type] || 'text-gray-600 border-gray-500';

              return (
                <div
                  key={tab.id}
                  className={cn(
                    "group flex items-center gap-2 px-3 py-1.5 rounded-t-lg cursor-pointer transition-all min-w-[120px] max-w-[200px]",
                    isActive 
                      ? `bg-white border-t-2 ${colorClass} shadow-sm` 
                      : "bg-gray-50 hover:bg-gray-100 border-t-2 border-transparent"
                  )}
                  onClick={() => setActiveTabId(tab.id)}
                >
                  <Icon className={cn("h-4 w-4 flex-shrink-0", isActive ? colorClass.split(' ')[0] : "text-gray-500")} />
                  <span className={cn(
                    "text-sm truncate flex-1",
                    isActive ? "font-medium text-gray-800" : "text-gray-600"
                  )}>
                    {tab.title}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeTab(tab.id);
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Tab Content - Render ALL tabs but only show active one (preserves state) */}
      <div className="flex-1 overflow-hidden bg-gray-50 relative">
        {tabs.map(tab => (
          <div 
            key={tab.id}
            className={`absolute inset-0 overflow-auto ${tab.id === activeTabId ? 'z-10' : 'z-0 pointer-events-none'}`}
            style={{ 
              visibility: tab.id === activeTabId ? 'visible' : 'hidden',
              opacity: tab.id === activeTabId ? 1 : 0
            }}
          >
            {renderTabContent(tab)}
          </div>
        ))}
      </div>
    </div>
  );
}
